import { ClientData } from '@/components/clients/types';
import { Client } from '@/types/client';
import { calculateBMI, getBMICategory, calculateBMR } from '@/utils/health-calculations';
import { differenceInYears } from 'date-fns';

// Re-export health calculation functions for convenience
export { calculateBMI, getBMICategory, calculateBMR } from '@/utils/health-calculations';

/**
 * Centralized client data service - Single source of truth for all client attributes
 * This service provides computed properties and ensures consistency across the application
 */

// =========================
// NAME HANDLING
// =========================

/**
 * Get the full name of a client
 */
export const getFullName = (client: Client): string => {
  const firstName = client.firstName?.trim() || '';
  const lastName = client.lastName?.trim() || '';
  return `${firstName} ${lastName}`.trim();
};

/**
 * Get client initials for avatar display
 */
export const getClientInitials = (client: Client): string => {
  const firstName = client.firstName?.trim() || '';
  const lastName = client.lastName?.trim() || '';
  return `${firstName[0] || ''}${lastName[0] || ''}`.toUpperCase();
};

// =========================
// AGE CALCULATIONS
// =========================

/**
 * Calculate age from date of birth
 */
export const getAge = (client: Client): number | null => {
  if (!client.dateOfBirth) return null;
  
  try {
    const birthDate = new Date(client.dateOfBirth);
    const today = new Date();
    return differenceInYears(today, birthDate);
  } catch (error) {
    console.warn('Invalid date of birth format:', client.dateOfBirth);
    return null;
  }
};

// =========================
// HEALTH METRICS
// =========================

/**
 * Calculate BMI for a client
 */
export const getClientBMI = (client: Client): number | null => {
  if (!client.weightKg || !client.heightCm) return null;
  return calculateBMI(client.weightKg, client.heightCm);
};

/**
 * Get BMI category for a client
 */
export const getClientBMICategory = (client: Client): string | null => {
  const bmi = getClientBMI(client);
  if (!bmi) return null;
  return getBMICategory(bmi);
};

/**
 * Calculate BMR for a client
 */
export const getClientBMR = (client: Client): number | null => {
  const { weightKg: weight, heightCm: height, gender } = client;
  const age = getAge(client as any);
  
  if (!weight || !height || !age || !gender) return client.bmr || null;
  
  return calculateBMR(weight, height, age, gender);
};

/**
 * Get all health metrics at once
 */
export const getClientHealthMetrics = (client: Client) => {
  const age = getAge(client as any);
  const bmi = getClientBMI(client);
  const bmiCategory = getClientBMICategory(client);
  const bmr = getClientBMR(client);
  
  return {
    age,
    bmi,
    bmiCategory,
    bmr
  };
};

// =========================
// ENERGY REQUIREMENTS
// =========================

/**
 * Get EER for a client (from stored value or default)
 */
export const getClientEER = (client: ClientData): number | null => {
  return client.eer || null;
};

/**
 * Get BMR for a client (calculated or stored)
 */
export const getClientBMRValue = (client: Client): number | null => {
  // Try calculated first, then stored value
  return getClientBMR(client) || client.bmr || null;
};

// =========================
// COMPUTED CLIENT DATA
// =========================

/**
 * Get all computed client data in a single object
 * This provides a complete view of the client with all computed fields
 */
export interface ComputedClientData extends ClientData {
  computed: {
    fullName: string;
    initials: string;
    age: number | null;
    bmi: number | null;
    bmiCategory: string | null;
    bmr: number | null;
    eer: number | null;
  };
}

export const getComputedClientData = (client: Client): ComputedClientData => {
  return {
    ...(client as any),
    computed: {
      fullName: getFullName(client),
      initials: getClientInitials(client),
      age: getAge(client),
      bmi: getClientBMI(client as any),
      bmiCategory: getClientBMICategory(client as any),
      bmr: getClientBMRValue(client as any),
      eer: getClientEER(client as any)
    }
  };
};

// =========================
// VALIDATION HELPERS
// =========================

/**
 * Check if client has required data for health calculations
 */
export const hasRequiredHealthData = (client: ClientData): boolean => {
  return !!(client.weightKg && client.heightCm && (client.age || client.dateOfBirth) && client.gender);
};

/**
 * Get missing fields for health calculations
 */
export const getMissingHealthFields = (client: ClientData): string[] => {
  const missing: string[] = [];
  
  if (!client.weightKg) missing.push('weight');
  if (!client.heightCm) missing.push('height');
  if (!client.age && !client.dateOfBirth) missing.push('age or date of birth');
  if (!client.gender) missing.push('gender');
  
  return missing;
};